import type { AsyncTransactionsFlushedEvent, ColDef, GridApi, IRowNode, RowNodeTransaction } from 'ag-grid-community';
import type { SizeValue } from '../BlotterTable/aggFuncs/types';
import type { SiblingLeafPrecision } from '../BlotterTable/columns/size.types';
import {
  getAlignedSiblingIncrement,
  getImmediateParent,
  refreshSiblingPrecisionCells,
  type SiblingPrecisionColumnConfig,
} from './getSiblingLeafCandidates';

/** Cache sentinel when a parent has no shared sibling alignment (single leaf / no match). */
const OWN_INCREMENT = 'own';

/** Parent group → colId → last known aligned increment (or OWN_INCREMENT). */
export type AlignmentCache = WeakMap<IRowNode, Map<string, string>>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Reads siblingLeafPrecision from size `cellRendererParams` when it is a plain object. */
function readSiblingLeafPrecision(colDef: ColDef): SiblingLeafPrecision | undefined {
  const params = colDef.cellRendererParams;
  if (!isPlainObject(params)) {
    return undefined;
  }
  const siblingLeafPrecision = params.siblingLeafPrecision;
  if (!isPlainObject(siblingLeafPrecision)) {
    return undefined;
  }
  const parentKeyPrefix = siblingLeafPrecision.parentKeyPrefix;
  if (typeof parentKeyPrefix !== 'string' || parentKeyPrefix === '') {
    return undefined;
  }
  return { parentKeyPrefix };
}

/** Finds every size column that opts into sibling-leaf precision via cellRendererParams. */
function getSiblingPrecisionColumnConfigs(api: GridApi): SiblingPrecisionColumnConfig[] {
  const configs: SiblingPrecisionColumnConfig[] = [];
  for (const column of api.getColumns() ?? []) {
    const siblingLeafPrecision = readSiblingLeafPrecision(column.getColDef());
    if (siblingLeafPrecision == null) {
      continue;
    }
    const colId = column.getColId();
    if (colId === '') {
      continue;
    }
    configs.push({ colId, ...siblingLeafPrecision });
  }
  return configs;
}

function getCachedAligned(cache: AlignmentCache, parent: IRowNode, colId: string): string | undefined {
  return cache.get(parent)?.get(colId);
}

function setCachedAligned(cache: AlignmentCache, parent: IRowNode, colId: string, value: string): void {
  let byColId = cache.get(parent);
  if (byColId == null) {
    byColId = new Map();
    cache.set(parent, byColId);
  }
  byColId.set(colId, value);
}

/** Narrows an async-transaction result to a row add/update/remove transaction. */
function isRowNodeTransaction(result: unknown): result is RowNodeTransaction {
  return (
    result != null &&
    typeof result === 'object' &&
    'add' in result &&
    Array.isArray((result as RowNodeTransaction).add) &&
    Array.isArray((result as RowNodeTransaction).update) &&
    Array.isArray((result as RowNodeTransaction).remove)
  );
}

/** Counts every leaf row currently in the grid. */
function countLeafNodes(api: GridApi): number {
  let count = 0;
  api.forEachLeafNode(() => {
    count += 1;
  });
  return count;
}

/**
 * True when every leaf in the grid was just added (initial/full snapshot / pause-resume reload).
 * A 1-row add into an existing group has add.length === 1 and leafCount > 1, so it is not skipped.
 */
function isFullAddSnapshot(api: GridApi, transactions: RowNodeTransaction[]): boolean {
  let addCount = 0;
  let updateCount = 0;
  let removeCount = 0;
  for (const transaction of transactions) {
    addCount += transaction.add.length;
    updateCount += transaction.update.length;
    removeCount += transaction.remove.length;
  }
  if (updateCount > 0 || removeCount > 0 || addCount === 0) {
    return false;
  }
  return addCount === countLeafNodes(api);
}

/** Reads increment and numeric value from a leaf cell for alignment. */
function getCandidateFromLeaf(api: GridApi, leaf: IRowNode, colId: string) {
  const siblingValue = api.getCellValue<SizeValue>({ rowNode: leaf, colKey: colId });
  return {
    increment: siblingValue?.increment,
    value: siblingValue?.value == null || siblingValue.value === '' ? undefined : String(siblingValue.value),
  };
}

/** Immediate parents of touched nodes, each once. Parent identity does not depend on column. */
function uniqueImmediateParents(nodes: IRowNode[]): IRowNode[] {
  const parents = new Set<IRowNode>();
  for (const node of nodes) {
    const parent = getImmediateParent(node);
    if (parent != null) {
      parents.add(parent);
    }
  }
  return [...parents];
}

/** Shared increment for a parent's leaves, or OWN_INCREMENT when there is none. */
function computeAlignedCacheValue(api: GridApi, parent: IRowNode, config: SiblingPrecisionColumnConfig): string {
  const leaves = parent.allLeafChildren ?? [];
  const leaf = leaves.find(node => !node.group);
  if (leaf == null) {
    return OWN_INCREMENT;
  }
  const aligned = getAlignedSiblingIncrement(leaf, node => getCandidateFromLeaf(api, node, config.colId), config);
  return aligned ?? OWN_INCREMENT;
}

/** Force-refreshes opted-in size columns on currently rendered rows, without flashing. */
function refreshRenderedOptedInColumns(api: GridApi, columnConfigs: SiblingPrecisionColumnConfig[]): void {
  const rendered = api.getRenderedNodes();
  if (rendered.length === 0 || columnConfigs.length === 0) {
    return;
  }
  api.refreshCells({
    rowNodes: rendered,
    columns: columnConfigs.map(config => config.colId),
    force: true,
    suppressFlash: true,
  });
}

/**
 * After AG Grid flushes async row transactions, keep sibling-leaf size precision in sync.
 *
 * Discovers opted-in size columns from `cellRendererParams.siblingLeafPrecision` on each flush,
 * then restamps `alignmentCache` once per unique immediate parent per colId. Touched add/update/remove
 * nodes that share a parent do not re-walk that parent's leaves.
 *
 * Refresh policy:
 * - Parentless remove (detached leaf): force-refresh opted-in columns on rendered rows.
 * - Full snapshot (every current leaf was just added): stamp cache, then refresh rendered
 *   cells on opted-in columns. Unrendered rows align on first paint; already-visible rows
 *   may have painted while their parent still had a single leaf.
 * - Incremental: `refreshSiblingPrecisionCells` for parents whose cached increment changed
 *   (or was missing) on that column. Unchanged alignment is left alone so AG Grid does not
 *   redraw siblings whose own cell values did not move.
 */
export function handleSiblingLeafPrecisionFlush(
  api: GridApi,
  event: AsyncTransactionsFlushedEvent,
  alignmentCache: AlignmentCache
): void {
  if (api.isDestroyed()) {
    return;
  }

  const columnConfigs = getSiblingPrecisionColumnConfigs(api);
  if (columnConfigs.length === 0) {
    return;
  }

  const transactions = event.results.filter(isRowNodeTransaction);
  if (transactions.length === 0) {
    return;
  }

  const fullSnapshot = isFullAddSnapshot(api, transactions);

  const touchedNodes: IRowNode[] = [];
  let hasParentlessRemove = false;

  for (const transaction of transactions) {
    for (const node of transaction.add) {
      touchedNodes.push(node);
    }
    for (const node of transaction.update) {
      touchedNodes.push(node);
    }
    for (const node of transaction.remove) {
      touchedNodes.push(node);
      if (node.parent == null && !node.group) {
        hasParentlessRemove = true;
      }
    }
  }

  if (touchedNodes.length === 0) {
    return;
  }

  const uniqueParents = uniqueImmediateParents(touchedNodes);

  if (hasParentlessRemove) {
    // Fall back: refresh visible cells on opted-in columns when remove nodes lost their parent.
    for (const config of columnConfigs) {
      for (const parent of uniqueParents) {
        setCachedAligned(alignmentCache, parent, config.colId, computeAlignedCacheValue(api, parent, config));
      }
    }
    refreshRenderedOptedInColumns(api, columnConfigs);
    return;
  }

  const parentsToRefreshByColId = new Map<string, Set<IRowNode>>();

  for (const config of columnConfigs) {
    for (const parent of uniqueParents) {
      const nextAligned = computeAlignedCacheValue(api, parent, config);
      const previousAligned = getCachedAligned(alignmentCache, parent, config.colId);
      setCachedAligned(alignmentCache, parent, config.colId, nextAligned);

      if (fullSnapshot) {
        continue;
      }

      if (previousAligned === undefined || previousAligned !== nextAligned) {
        let parents = parentsToRefreshByColId.get(config.colId);
        if (parents == null) {
          parents = new Set();
          parentsToRefreshByColId.set(config.colId, parents);
        }
        parents.add(parent);
      }
    }
  }

  if (fullSnapshot) {
    refreshRenderedOptedInColumns(api, columnConfigs);
    return;
  }

  if (parentsToRefreshByColId.size === 0) {
    return;
  }

  for (const config of columnConfigs) {
    const parents = parentsToRefreshByColId.get(config.colId);
    if (parents == null || parents.size === 0) {
      continue;
    }
    refreshSiblingPrecisionCells(api, [...parents], [config]);
  }
}
